import React, { useState } from "react";

import { Dialog } from "../Dialog";
import { Island } from "../Island";
import { t } from "../../i18n";
import {
  getUserPreferences,
  saveUserPreferences,
  resetUserPreferences,
  type UserPreferences,
} from "../../userPreferences";

import { FontPresetSettings } from "./FontPresetSettings";
import { AppearanceSettings } from "./AppearanceSettings";
import { LanguageSettings } from "./LanguageSettings";
import { HelpSettings } from "./HelpSettings";

import "./SettingsDialog.scss";

interface SettingsDialogProps {
  onClose: () => void;
}

type SettingsTab = "appearance" | "fontPresets" | "language" | "help";

export const SettingsDialog: React.FC<SettingsDialogProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>("appearance");
  const [preferences, setPreferences] = useState<UserPreferences>(
    getUserPreferences(),
  );

  const handleSave = () => {
    saveUserPreferences(preferences);
    onClose();
    // Reload to apply changes
    window.location.reload();
  };

  const handleReset = () => {
    if (confirm(t("settings.confirmReset"))) {
      resetUserPreferences();
      setPreferences(getUserPreferences());
    }
  };

  const updatePreferences = (updates: Partial<UserPreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      ...updates,
      fontPresets: {
        ...prev.fontPresets,
        ...(updates.fontPresets || {}),
      },
    }));
  };

  return (
    <Dialog onCloseRequest={onClose} title={t("settings.title")} size="wide">
      <div className="SettingsDialog">
        <div className="SettingsDialog__tabs">
          <button
            className={activeTab === "appearance" ? "active" : ""}
            onClick={() => setActiveTab("appearance")}
          >
            {t("settings.appearance")}
          </button>
          <button
            className={activeTab === "fontPresets" ? "active" : ""}
            onClick={() => setActiveTab("fontPresets")}
          >
            {t("settings.fontPresets")}
          </button>
          <button
            className={activeTab === "language" ? "active" : ""}
            onClick={() => setActiveTab("language")}
          >
            {t("settings.language")}
          </button>
          <button
            className={activeTab === "help" ? "active" : ""}
            onClick={() => setActiveTab("help")}
          >
            {t("settings.help")}
          </button>
        </div>

        <div className="SettingsDialog__content">
          <Island padding={4}>
            {activeTab === "appearance" && (
              <AppearanceSettings
                preferences={preferences}
                onChange={updatePreferences}
              />
            )}
            {activeTab === "fontPresets" && (
              <FontPresetSettings
                preferences={preferences}
                onChange={updatePreferences}
              />
            )}
            {activeTab === "language" && (
              <LanguageSettings
                preferences={preferences}
                onChange={updatePreferences}
              />
            )}
            {activeTab === "help" && <HelpSettings />}
          </Island>
        </div>

        <div className="SettingsDialog__actions">
          <button onClick={handleReset} className="SettingsDialog__reset">
            {t("settings.reset")}
          </button>
          <div className="SettingsDialog__actions-right">
            <button onClick={onClose} className="SettingsDialog__cancel">
              {t("buttons.cancel")}
            </button>
            <button onClick={handleSave} className="SettingsDialog__save">
              {t("buttons.save")}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
